import React, { useState, useEffect } from "react";
import { Subject, Level } from "../../../../services/Dtos";
import { VStack, Box } from "@chakra-ui/react";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import CustomAlert from "../../../CustomUi/CustomAlert";
import useIcons from "../../../../hooks/useIcons";
import CustomModal from "../../../CustomUi/CustomModal";
import TextInput from "../../../CustomUi/TextInput";
import SelectInput from "../../../CustomUi/SelectInput";
import TextAreaInput from "../../../CustomUi/TextAreaInput";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useSubjectService } from "../../../../hooks/Domain/useSubjectService";
import InlineSeparator from "../../../CustomUi/InlineSeparator";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  subjectToEdit: Subject | null;
  subjectLevel: Level | null;
}

const SubjectModal: React.FC<Props> = ({
  isOpen,
  onClose,
  subjectToEdit,
  subjectLevel,
}) => {
  const {
    createSubject,
    updateSubject,
    deleteSubject,
    openDeleteDialog,
    setOpenDeleteDialog,
    errors,
    clearErrors,
    isLoading,
  } = useSubjectService();

  const getInitialSubjectFormState = (): Subject => ({
    id: "",
    subjectName: "",
    description: "",
    levelId: subjectLevel?.id || "",
    schoolId: "",
    createdAt: "",
    updatedAt: "",
  });

  const initialSubjectFormState = getInitialSubjectFormState();

  const { levels } = useSchoolsContext();
  const icons = useIcons();
  const [subjectFormState, setSubjectFormState] = useState<Subject>(
    initialSubjectFormState
  );
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    subjectName: { type: "text" as const, required: true, maxLength: 100 },
    levelId: { type: "text" as const, required: true },
    description: { type: "text" as const, required: false, maxLength: 500 },
  } as const;

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: subjectToEdit,
      currentData: subjectFormState,
      isEditMode: !!subjectToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setSubjectFormState,
    setTouchedFields,
  });

  // Real-time validation
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof Subject;
    return validateField(fieldKey, value);
  };

  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (subjectToEdit) {
      setSubjectFormState(subjectToEdit);
    } else {
      setSubjectFormState(getInitialSubjectFormState());
    }
    setTouchedFields(new Set());
  }, [subjectToEdit, subjectLevel, isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      subjectFormState[fieldName as keyof Subject]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      subjectFormState[fieldName as keyof Subject]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges) return;

    if (subjectToEdit) {
      const response = await updateSubject(subjectToEdit.id, subjectFormState);
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createSubject(subjectFormState);
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDelete = async () => {
    if (!subjectToEdit) return;
    const response = await deleteSubject(subjectToEdit.id);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && subjectToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  const levelOptions = levels.map((level) => ({
    label: level.levelName,
    value: level.id,
  }));

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDelete}
        deleteMessage="All related subject records and student assignments will be lost permanently"
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={subjectToEdit ? "Edit Subject" : "Add New Subject"}
        icon={icons.main.subject}
        mode={subjectToEdit ? "edit" : "add"}
        actionText={subjectToEdit ? "Save Changes" : "Save Subject"}
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
      >
        {errors.length > 0 && (
          <VStack gap={2} mb={4}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          {/* Subject Information Section */}
          <Box>
            <InlineSeparator label="Subject Information" />
            <VStack gap={4}>
              <TextInput
                label="Subject Name"
                name="subjectName"
                type="text"
                value={subjectFormState.subjectName}
                onChange={handleFieldChange}
                placeholder="Subject Name"
                required
                error={
                  showFieldError("subjectName")
                    ? getFieldError("subjectName")
                    : ""
                }
                invalid={showFieldError("subjectName")}
               
              />

              {subjectToEdit && (
                <SelectInput
                  label="Subject Level"
                  name="levelId"
                  value={subjectFormState.levelId}
                  onChange={handleFieldChange}
                  collection={levelOptions}
                  placeholder="Select Subject Level"
                  required
                  error={
                    showFieldError("levelId") ? getFieldError("levelId") : ""
                  }
                  invalid={showFieldError("levelId")}
                />
              )}
            </VStack>
          </Box>



          {/* Additional Information Section */}
          <Box>
            <InlineSeparator label="Additional Information" />
            <TextAreaInput
              label="Description"
              name="description"
              value={subjectFormState.description}
              onChange={handleFieldChange}
              rows={3}
              error={
                showFieldError("description")
                  ? getFieldError("description")
                  : ""
              }
              invalid={showFieldError("description")}
            />
          </Box>
        </VStack>
      </CustomModal>
    </>
  );
};

export default SubjectModal;
